import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  SimpleGrid,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useColorModeValue,
  useDisclosure,
  Checkbox,
} from '@chakra-ui/react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiEdit2, FiFilter, FiPlus, FiSearch, FiUsers } from 'react-icons/fi';
import { ResearcherCard } from './ResearcherCard';
import { useResearchers, useResearcherProfile } from '../../hooks/useResearchers';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { RESEARCH_TYPES } from '../../utils/formatters';

const STORAGE_KEY = 'researcherTypeFilters';

function loadSavedFilters() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return [];
}

export default function ResearcherList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState(loadSavedFilters);
  const [sortBy, setSortBy] = useState('newest');

  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { profile: myProfile, loading: profileLoading } = useResearcherProfile(currentUser?.uid);
  const { isOpen: filtersOpen, onToggle: toggleFilters } = useDisclosure();

  const { researchers, loading, loadingMore, hasMore, loadMore, error } = useResearchers();
  const sentinelRef = useRef(null);

  const headerBg = useColorModeValue('white', 'gray.800');
  const filtersActive = activeFilters.length > 0 || searchQuery.trim() || sortBy !== 'newest';

  const toggleFilter = useCallback((key) => {
    setActiveFilters((prev) => {
      const next = prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const removeFilter = useCallback((key) => {
    setActiveFilters((prev) => {
      const next = prev.filter((k) => k !== key);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0, rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loading, hasMore, loadingMore, loadMore]);

  const hasFilters = activeFilters.length > 0;

  const filtered = researchers
    .filter((r) => {
      if (!hasFilters) return true;
      return (r.services || []).some((s) => activeFilters.includes(s));
    })
    .filter((r) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        (r.displayName || '').toLowerCase().includes(q) ||
        (r.bio || '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      }
      if (sortBy === 'rating') {
        return (b.reputation?.rating || 0) - (a.reputation?.rating || 0);
      }
      return 0;
    });

  return (
    <Container maxW="7xl" py={6} px={0}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Heading size="lg">Browse Researchers</Heading>
            <Text color="gray.600" mt={1}>
              {loading ? '' : `${filtered.length} researcher${filtered.length !== 1 ? 's' : ''} available`}
            </Text>
          </Box>
          <HStack spacing={2}>
            <IconButton
              display={{ base: 'flex', md: 'none' }}
              icon={<Icon as={FiFilter} />}
              variant={filtersActive ? 'solid' : 'outline'}
              colorScheme={filtersActive ? 'orange' : 'gray'}
              aria-label="Toggle filters"
              onClick={toggleFilters}
              size="md"
            />
            {!profileLoading && (
              <Button
                onClick={() => navigate('/researchers/create')}
                colorScheme="orange"
                leftIcon={<Icon as={myProfile ? FiEdit2 : FiPlus} />}
                size="md"
                flexShrink={0}
              >
                {myProfile ? 'Edit Profile' : 'Publish Profile'}
              </Button>
            )}
          </HStack>
        </HStack>

        {/* Filters */}
        <Box
          display={{ base: filtersOpen ? 'block' : 'none', md: 'block' }}
          bg={headerBg}
          rounded="xl"
          p={4}
          boxShadow="sm"
          borderWidth={1}
          borderColor={useColorModeValue('gray.100', 'gray.700')}
        >
          <VStack spacing={3} align="stretch">
            <HStack spacing={4} flexWrap="wrap" gap={2}>
              <InputGroup maxW={{ base: 'full', md: '300px' }}>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search researchers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>

              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  variant="outline"
                  rightIcon={<Icon as={FiChevronDown} />}
                  size="md"
                  fontWeight="normal"
                  minW="180px"
                  textAlign="left"
                >
                  Filter by specialty
                </MenuButton>
                <MenuList zIndex={10}>
                  {Object.entries(RESEARCH_TYPES).map(([key, { label }]) => {
                    const isChecked = activeFilters.includes(key);
                    return (
                      <MenuItem
                        key={key}
                        onClick={() => toggleFilter(key)}
                        closeOnSelect={false}
                      >
                        <Checkbox
                          isChecked={isChecked}
                          pointerEvents="none"
                          colorScheme="orange"
                          mr={2}
                        />
                        {label}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>

              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                maxW={{ base: 'full', md: '180px' }}
              >
                <option value="newest">Newest first</option>
                <option value="rating">Highest rated</option>
              </Select>
            </HStack>

            {hasFilters && (
              <HStack spacing={2} align="center" flexWrap="wrap">
                <Text fontSize="sm" fontWeight="semibold" color="gray.500" whiteSpace="nowrap">
                  Specialty Filters:
                </Text>
                <Wrap spacing={2}>
                  {activeFilters.map((key) => {
                    const type = RESEARCH_TYPES[key];
                    if (!type) return null;
                    return (
                      <WrapItem key={key}>
                        <Tag
                          size="md"
                          colorScheme={type.color}
                          variant="solid"
                          borderRadius="full"
                        >
                          <TagLabel>{type.label}</TagLabel>
                          <TagCloseButton onClick={() => removeFilter(key)} />
                        </Tag>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              </HStack>
            )}
          </VStack>
        </Box>

        {/* Grid */}
        {loading ? (
          <LoadingSpinner message="Loading researchers..." />
        ) : error ? (
          <EmptyState
            title="Error loading researchers"
            description={error}
            icon={FiUsers}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No researchers found"
            description={searchQuery || hasFilters
              ? "Try adjusting your filters"
              : "Be the first to publish a researcher profile!"}
            icon={FiUsers}
          />
        ) : (
          <>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={5}>
              {filtered.map((researcher) => (
                <ResearcherCard key={researcher.id} researcher={researcher} />
              ))}
            </SimpleGrid>
            {hasMore && <Box ref={sentinelRef} h="1px" />}
            {loadingMore && (
              <HStack justify="center" py={4}>
                <Spinner size="sm" color="orange.500" />
                <Text fontSize="sm" color="gray.500">Loading more researchers...</Text>
              </HStack>
            )}
          </>
        )}
      </VStack>
    </Container>
  );
}
